import { useState, type FC, memo } from 'react';
import { Layout, Typography } from 'antd';
import { useBreakpoint } from '../../hooks/use-media-query';

const { Header } = Layout;
const { Title } = Typography;

export interface AppHeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  logoSrc?: string;
  fallbackLogoSrc?: string;
}

// Breakpoint-based header configuration
const getHeaderConfig = (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop') => {
  switch (breakpoint) {
    case 'mobile':
      return {
        height: 'clamp(36px, 8vh, 48px)',
        padding: 'clamp(6px, 1.5vh, 12px) 16px',
        logoWidth: 'clamp(70px, 28vw, 100px)',
        logoHeight: 'clamp(14px, 3.5vh, 22px)',
        fontSize: 'clamp(14px, 3.5vh, 20px)',
        gap: 'clamp(6px, 2vw, 10px)',
      };
    case 'tablet':
      return {
        height: '40px',
        padding: '10px 28px',
        logoWidth: '96px',
        logoHeight: '19px',
        fontSize: '19px',
        gap: '10px',
      };
    case 'desktop':
      return {
        height: '44px',
        padding: '11px 32px',
        logoWidth: '104px',
        logoHeight: '21px',
        fontSize: '21px',
        gap: '11px',
      };
    case 'largeDesktop':
      return {
        height: '52px',
        padding: '13px 40px',
        logoWidth: '120px',
        logoHeight: '24px',
        fontSize: '24px',
        gap: '12px',
      };
    default:
      return {
        height: '40px',
        padding: '10px 28px',
        logoWidth: '96px',
        logoHeight: '19px',
        fontSize: '19px',
        gap: '10px',
      };
  }
};

/**
 * AppHeader - Shared Header Component
 * Consistent header across all pages with logo and title
 * Progressive sizing: mobile → tablet → desktop → largeDesktop
 */
export const AppHeader: FC<AppHeaderProps> = memo(({
  title = 'Counter',
  showBackButton = false,
  onBackClick,
  logoSrc,
  fallbackLogoSrc = '/vite.svg',
}) => {
  const [logoError, setLogoError] = useState(false);
  const breakpoint = useBreakpoint();
  const config = getHeaderConfig(breakpoint);

  const handleHeaderClick = () => {
    if (showBackButton && onBackClick) {
      onBackClick();
    }
  };

  const currentLogoSrc = logoError ? fallbackLogoSrc : (logoSrc || fallbackLogoSrc);

  return (
    <Header
      onClick={handleHeaderClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: '#2f2f31',
        padding: config.padding,
        borderBottom: '1px solid #4a4a4d',
        height: config.height,
        lineHeight: config.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        cursor: showBackButton ? 'pointer' : 'default',
      }}
    >
      <Title
        level={2}
        style={{
          color: '#fff',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: config.gap }}>
          {/* Fixed width container to prevent layout shift - responsive logo size */}
          <div
            style={{
              width: config.logoWidth,
              height: config.logoHeight,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src={currentLogoSrc}
              onError={() => setLogoError(true)}
              alt="FISHSCOPE"
              loading="eager"
              style={{
                height: config.logoHeight,
                width: config.logoWidth,
                userSelect: 'none',
                display: 'block',
                verticalAlign: 'middle',
                opacity: 1,
                objectFit: 'contain',
              }}
            />
          </div>
          <span
            style={{
              fontFamily: 'Noto Sans',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: config.fontSize,
              lineHeight: '100%',
              letterSpacing: '0%',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            {title}
          </span>
        </div>
      </Title>

      {showBackButton && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <svg
            width="11"
            height="14"
            viewBox="0 0 11 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              userSelect: 'none',
            }}
          >
            <path
              d="M8.5 1L2 7L8.5 13"
              stroke="#FFFFFF"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </Header>
  );
});

AppHeader.displayName = 'AppHeader';
